/**
 * JSON:API Utility Functions
 *
 * Handles transformation between JSON:API format and flat JavaScript objects
 *
 * JSON:API Format:
 * {
 *   data: {
 *     type: "resource-type",
 *     id: "uuid",
 *     attributes: { field1: "value", field2: "value" },
 *     relationships: { ... }
 *   }
 * }
 *
 * Flat Format:
 * {
 *   id: "uuid",
 *   field1: "value",
 *   field2: "value"
 * }
 */

/**
 * Normalize a single JSON:API resource object to flat format
 * @param {Object} resource - JSON:API resource object
 * @returns {Object} Flattened object with id and attributes at top level
 */
export function normalizeResource(resource) {
  if (!resource) return null

  // Already flat format
  if (!resource.attributes && !resource.type) {
    return resource
  }

  // Start with flattened attributes
  const normalized = {
    id: resource.id,
    type: resource.type,
    ...resource.attributes,
  }

  // Extract relationship data directly (keep id and type)
  if (resource.relationships) {
    Object.entries(resource.relationships).forEach(([key, relationship]) => {
      if (relationship.data !== undefined) {
        // Store the relationship data as-is (with id and type)
        // Handle null relationships (valid JSON:API for empty relationships)
        normalized[key] = relationship.data
      }
    })

    // Note: We intentionally do NOT store _relationships to avoid duplication.
    // The relationship data is already stored in simplified format above.
    // e.g., category: { type: "category", id: "123" }
    // Storing _relationships would duplicate this as:
    // _relationships: { category: { data: { type: "category", id: "123" } } }
    // This saves memory and prevents confusion about which format to use.
  }

  return normalized
}

/**
 * Normalize JSON:API response data (handles both single and array responses)
 * @param {Object|Array} data - JSON:API data object or array
 * @returns {Object|Array|null} Normalized flat object(s)
 */
export function normalizeData(data) {
  if (!data) return null

  // Handle array of resources
  if (Array.isArray(data)) {
    return data.map(normalizeResource)
  }

  // Handle single resource
  return normalizeResource(data)
}

/**
 * Normalize a complete JSON:API response envelope
 * @param {Object} response - Axios response object
 * @returns {Object|Array|null} Normalized data
 */
export function normalizeResponse(response) {
  if (!response || !response.data) return null

  // Standard JSON:API response with data envelope
  if (response.data.data !== undefined) {
    return normalizeData(response.data.data)
  }

  // Direct data response (already flat)
  return response.data
}

/**
 * Serialize a flat object to JSON:API format for requests
 * @param {Object} data - Flat data object
 * @param {string} type - Resource type (e.g., 'categories', 'metrics')
 * @param {string} [id] - Resource ID (for updates)
 * @returns {Object} JSON:API formatted object
 */
export function serializeResource(data, type, id = null) {
  const { type: _, _relationships, ...attributes } = data

  const resource = {
    type,
    attributes
  }

  if (id) {
    resource.id = id
  }

  return { data: resource }
}

/**
 * Extract errors from various error response formats
 * Handles: JSON:API, DRF, Django middleware, custom formats
 * @param {Object} errorResponse - Error response from API (axios error.response or data)
 * @returns {Array} Array of normalized error objects with title, detail, code, source, status
 */
/**
 * A readable error for an HTML error page (Django's own 404/500 pages, a proxy's
 * error page), chosen by status. Every HTML response used to read "A critical
 * server error occurred" - including a plain 404 for a route that did not
 * exist, which sent people looking for an outage (Sentry GRAPHIT-56).
 *
 * @param {number} status
 * @returns {{title: string, detail: string, status: number}}
 */
function htmlErrorFor(status) {
  if (status === 404) {
    return {
      title: 'Not found',
      detail: "We couldn't find what you were looking for. Please refresh the page and try again.",
      status,
    }
  }
  if (status >= 500) {
    return {
      title: 'Server error',
      detail: 'A critical server error occurred. Please try again later.',
      status,
    }
  }
  return {
    title: 'Error',
    detail: 'Something went wrong. Please try again.',
    status,
  }
}

export function normalizeErrors(errorResponse) {
  if (!errorResponse) return []

  // If passed an axios error.response object, extract data
  const data = errorResponse.data || errorResponse
  const status = errorResponse.status || data.status

  // Format 1: JSON:API errors array
  // { errors: [{ status: '400', code: 'validation_error', title: 'Validation Error', detail: 'Invalid...' }] }
  if (data.errors && Array.isArray(data.errors)) {
    return data.errors.map(error => ({
      title: error.title || 'Error',
      detail: error.detail || error.message || 'An error occurred',
      code: error.code,
      source: error.source || {},
      status: error.status || status,
      // meta carries the part of an error a user can act on - which plan limit
      // was hit and by how much, or which dashboards still use a metric.
      // Dropping it here silently defeated both: the upgrade prompt's usage bar
      // never rendered and the metric-in-use dialog listed nothing, while their
      // unit tests passed because those build the error object directly and
      // never go through this function.
      meta: error.meta || {}
    }))
  }

  // Format 2: Custom middleware format (common in our Django app)
  // { status: '400', code: 'validation_error', title: 'Validation Error', detail: 'Invalid email or password' }
  if (data.code && data.title && data.detail) {
    return [{
      title: data.title,
      detail: data.detail,
      code: data.code,
      status: data.status || status,
      meta: data.meta || {}
    }]
  }

  // Format 3: Simple error object with message
  // { error: 'error_code', message: 'Human readable message' }
  if (data.message && typeof data.message === 'string') {
    return [{
      title: 'Error',
      detail: data.message,
      code: data.error || data.code,
      status: status
    }]
  }

  // Format 4: Detail field (common in DRF)
  // { detail: 'Authentication credentials were not provided.' }
  if (data.detail && typeof data.detail === 'string') {
    return [{
      title: 'Error',
      detail: data.detail,
      code: data.code,
      status: status
    }]
  }

  // Format 5: Django REST Framework validation errors (field-based)
  // { field_name: ['Error message 1', 'Error message 2'], another_field: 'Single error' }
  if (data && typeof data === 'object' && !data.errors && !data.detail && !data.message) {
    const errors = []
    Object.entries(data).forEach(([field, messages]) => {
      // Skip non-error fields like 'status', 'code', etc.
      if (['status', 'code', 'error', 'title'].includes(field)) {
        return
      }

      if (Array.isArray(messages)) {
        messages.forEach(msg => {
          errors.push({
            title: 'Validation Error',
            detail: typeof msg === 'string' ? msg : JSON.stringify(msg),
            source: { pointer: `/data/attributes/${field}` },
            field: field,
            status: status
          })
        })
      } else if (typeof messages === 'string') {
        errors.push({
          title: 'Validation Error',
          detail: messages,
          source: { pointer: `/data/attributes/${field}` },
          field: field,
          status: status
        })
      }
    })
    if (errors.length > 0) {
      return errors
    }
  }

  // Format 6: Error string directly
  if (typeof data === 'string') {
    // Detect HTML responses (e.g. Django 500 error pages) and show a clean message
    const trimmed = data.trimStart().toLowerCase()
    if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
      console.error('Server returned HTML error response:', data)
      return [htmlErrorFor(status)]
    }
    return [{
      title: 'Error',
      detail: data,
      status: status
    }]
  }

  // Fallback: Generic error message
  return [{
    title: 'Error',
    detail: errorResponse.message || 'An unexpected error occurred',
    status: status
  }]
}

/**
 * Check if data is in JSON:API format
 * @param {*} data - Data to check
 * @returns {boolean} True if data appears to be JSON:API format
 */
export function isJsonApiFormat(data) {
  if (!data || typeof data !== 'object') return false

  // Check for JSON:API envelope
  if (data.data !== undefined) {
    const resource = Array.isArray(data.data) ? data.data[0] : data.data
    return resource && (resource.type !== undefined || resource.attributes !== undefined)
  }

  // Check for direct resource format
  return data.type !== undefined && data.attributes !== undefined
}

/**
 * Normalize included resources from JSON:API response
 * @param {Array} included - Array of included resources
 * @returns {Object} Map of resources by type and id
 */
export function normalizeIncluded(included) {
  if (!included || !Array.isArray(included)) return {}

  const normalized = {}

  included.forEach(resource => {
    const { type, id } = resource
    if (!normalized[type]) {
      normalized[type] = {}
    }
    normalized[type][id] = normalizeResource(resource)
  })

  return normalized
}

/**
 * Resolve relationships in a normalized resource using included data
 * @param {Object} resource - Normalized resource with _relationships
 * @param {Object} included - Normalized included resources map
 * @returns {Object} Resource with resolved relationships
 */
export function resolveRelationships(resource, included) {
  if (!resource._relationships || !included) return resource

  const resolved = { ...resource }

  Object.entries(resource._relationships).forEach(([key, relationship]) => {
    if (relationship.data) {
      if (Array.isArray(relationship.data)) {
        // Has-many relationship
        resolved[key] = relationship.data.map(ref =>
          included[ref.type]?.[ref.id] || ref
        )
      } else {
        // Has-one relationship
        const ref = relationship.data
        resolved[key] = included[ref.type]?.[ref.id] || ref
      }
    }
  })

  // Remove internal _relationships marker
  delete resolved._relationships

  return resolved
}

/**
 * Complete normalization with relationship resolution
 * @param {Object} response - Full Axios response
 * @returns {Object|Array|null} Fully normalized data with relationships resolved
 */
export function normalizeResponseWithRelationships(response) {
  if (!response || !response.data) return null

  const data = normalizeResponse(response)

  // If no included data, return normalized data as-is
  if (!response.data.included) return data

  const included = normalizeIncluded(response.data.included)

  // Resolve relationships
  if (Array.isArray(data)) {
    return data.map(resource => resolveRelationships(resource, included))
  }

  return resolveRelationships(data, included)
}

/**
 * Build JSON:API v1.1 compliant query parameters
 *
 * Supports:
 * - Filtering: filter[field]=value or filter[field.lookup]=value
 * - Sorting: sort=field1,-field2
 * - Includes: include=relation1,relation2
 * - Pagination: page[number]=X&page[size]=Y
 *
 * @param {Object} options - Query options
 * @param {Object} [options.filters] - Filters object
 *   Simple: { is_active: true, name: 'test' }
 *   Lookups: { name: { icontains: 'test' }, age: { gte: 18 } }
 * @param {Array<string>|string} [options.sort] - Sort fields
 *   Array: ['name', '-created_at']
 *   String: 'name,-created_at'
 *   Prefix with '-' for descending order
 * @param {Array<string>|string} [options.include] - Related resources to include
 *   Array: ['category', 'created_by']
 *   String: 'category,created_by'
 * @param {number} [options.page] - Page number (1-indexed)
 * @param {number} [options.pageSize] - Items per page
 *
 * @returns {Object} URL query parameters object for axios
 *
 * @example
 * buildJsonApiQuery({ filters: { is_active: true } })
 * // Returns: { 'filter[is_active]': 'true' }
 *
 * @example
 * buildJsonApiQuery({
 *   filters: { name: { icontains: 'sunday' } }
 * })
 * // Returns: { 'filter[name.icontains]': 'sunday' }
 *
 * @example
 * buildJsonApiQuery({
 *   filters: {
 *     name: { icontains: 'test' },
 *     is_active: true
 *   },
 *   sort: ['sort_order', '-created_at'],
 *   include: ['partition_type'],
 *   page: 2,
 *   pageSize: 20
 * })
 * // Returns: {
 * //   'filter[name.icontains]': 'test',
 * //   'filter[is_active]': 'true',
 * //   'sort': 'sort_order,-created_at',
 * //   'include': 'partition_type',
 * //   'page[number]': '2',
 * //   'page[size]': '20'
 * // }
 */
export function buildJsonApiQuery(options = {}) {
  const params = {}

  // Build filter parameters
  if (options.filters && typeof options.filters === 'object') {
    Object.entries(options.filters).forEach(([field, value]) => {
      // Skip null/undefined values
      if (value === null || value === undefined) {
        return
      }

      // Check if value is a lookup object (e.g., { icontains: 'test' })
      if (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date)
      ) {
        // Handle lookup filters: { name: { icontains: 'test' } }
        Object.entries(value).forEach(([lookup, lookupValue]) => {
          if (lookupValue !== null && lookupValue !== undefined) {
            params[`filter[${field}.${lookup}]`] = String(lookupValue)
          }
        })
      } else {
        // Handle simple equality filters: { is_active: true }
        params[`filter[${field}]`] = String(value)
      }
    })
  }

  // Build sort parameter
  if (options.sort) {
    const sortFields = Array.isArray(options.sort) ? options.sort : [options.sort]
    const validFields = sortFields.filter(f => f && typeof f === 'string')
    if (validFields.length > 0) {
      params.sort = validFields.join(',')
    }
  }

  // Build include parameter
  if (options.include) {
    const includeFields = Array.isArray(options.include)
      ? options.include
      : [options.include]
    const validFields = includeFields.filter(f => f && typeof f === 'string')
    if (validFields.length > 0) {
      params.include = validFields.join(',')
    }
  }

  // Build pagination parameters
  if (options.page !== undefined && options.page !== null) {
    params['page[number]'] = String(options.page)
  }
  if (options.pageSize !== undefined && options.pageSize !== null) {
    params['page[size]'] = String(options.pageSize)
  }

  return params
}

/**
 * Helper to disable automatic ?include=all for specific requests
 *
 * By default, the API client adds ?include=all to all requests.
 * Use this helper when you want a lightweight response without relationships.
 *
 * @param {Object} params - Existing query parameters
 * @returns {Object} Parameters with include disabled
 *
 * @example
 * // Disable all includes (lightweight response)
 * apiClient.get('/api/metrics/', { params: withoutIncludes() })
 *
 * @example
 * // Specific includes only
 * apiClient.get('/api/metrics/', {
 *   params: withoutIncludes({ include: 'category' })
 * })
 *
 * @example
 * // Combined with other params
 * apiClient.get('/api/metrics/', {
 *   params: withoutIncludes({
 *     'filter[is_active]': true,
 *     include: 'category'  // Override default 'all'
 *   })
 * })
 */
export function withoutIncludes(params = {}) {
  return {
    ...params,
    include: params.include !== undefined ? params.include : null
  }
}

/**
 * Helper to explicitly request specific includes
 *
 * @param {string|Array<string>} includes - Relationship(s) to include
 * @param {Object} params - Additional query parameters
 * @returns {Object} Parameters with specific includes
 *
 * @example
 * apiClient.get('/api/metrics/', {
 *   params: withIncludes(['category', 'created_by'])
 * })
 */
export function withIncludes(includes, params = {}) {
  const includeStr = Array.isArray(includes) ? includes.join(',') : includes
  return {
    ...params,
    include: includeStr
  }
}

export default {
  normalizeResource,
  normalizeData,
  normalizeResponse,
  normalizeResponseWithRelationships,
  serializeResource,
  normalizeErrors,
  normalizeIncluded,
  resolveRelationships,
  isJsonApiFormat,
  buildJsonApiQuery,
  withoutIncludes,
  withIncludes
}
